// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
// Cadastro de descrições padrão do S-26 (usadas nas sugestões de preenchimento).
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function S26DescriptionsDialog({ open, onOpenChange, onChanged }) {
  const [registered, setRegistered] = useState([]);
  const [newDesc, setNewDesc] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/audit-forms/s26/descriptions`)
      .then(({ data }) => setRegistered(data.registered || []))
      .catch(() => {});
  }, [open]);

  const handleAdd = async () => {
    const desc = newDesc.trim();
    if (!desc) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/audit-forms/s26/descriptions`, { description: desc });
      setRegistered((prev) => prev.some((r) => r.id === data.id) ? prev : [...prev, data].sort((a, b) => a.description.localeCompare(b.description)));
      setNewDesc('');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao cadastrar descrição');
    } finally { setBusy(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/audit-forms/s26/descriptions/${id}`);
      setRegistered((prev) => prev.filter((r) => r.id !== id));
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao remover descrição');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="s26-descriptions-dialog">
        <DialogHeader>
          <DialogTitle>Descrições cadastradas</DialogTitle>
          <DialogDescription>
            Cadastre descrições padrão para aparecerem nas sugestões ao preencher os lançamentos.
            As descrições já usadas nas folhas anteriores também são sugeridas automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            placeholder="Ex.: PAGAMENTO DE ENERGIA ELETRICA"
            maxLength={120}
            data-testid="s26-desc-new-input"
          />
          <Button onClick={handleAdd} disabled={busy || !newDesc.trim()} data-testid="s26-desc-add-btn">
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        <div className="max-h-64 overflow-y-auto space-y-1" data-testid="s26-desc-registered-list">
          {registered.length === 0 && (
            <p className="text-xs text-muted-foreground">Nenhuma descrição cadastrada ainda.</p>
          )}
          {registered.map((r) => (
            <div key={r.id} className="flex items-center justify-between border border-border rounded px-2 py-1.5 text-xs">
              <span className="truncate pr-2">{r.description}</span>
              <button
                onClick={() => handleDelete(r.id)}
                className="text-muted-foreground hover:text-red-500 shrink-0"
                title="Remover"
                data-testid={`s26-desc-delete-${r.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
